import { Terminal } from "@stripe/terminal-js";
import { CircleCheckFilledIcon, CircleCrossIcon, WarningFilledIcon } from "@xola/icons";
import { Button, EmvImage, flash, Modal, Spinner } from "@xola/ui-kit";
import { duration } from "common";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Except } from "type-fest";
import { Purchase } from "types";
import { EMVTerminal, useStripeTerminal } from "../../hooks/useStripeTerminal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { useMonitorRefund } from "../PurchaseModification/Refund/refundUpdateHelper";
import { getReaderStatus } from "./EmvInput";
import { PaymentInputRef, PaymentInputRefParams } from "./PaymentInputRef";
import { useModal } from "@/hooks/useVisibility";

interface EmvInputProps {
    initialPurchase: Purchase;
}

export enum RefundStatusType {
    PROCESSING = "processing",
    PRIMARY = "primary",
    DANGER = "danger",
    DELAYED = "delayed",
    SUCCESS = "success",
}

const defaultRefundStatus: { message: string; status: RefundStatusType } = {
    message: "Tap or insert card ending in ",
    status: RefundStatusType.PRIMARY,
};

export const EmvRefundInput = forwardRef<PaymentInputRef, EmvInputProps>(({ initialPurchase }, ref) => {
    const [refundStatus, setRefundStatus] = useState(defaultRefundStatus);
    const paramRef = useRef<PaymentInputRefParams>();
    const resolveConfirmRef = useRef<(value?: unknown) => void>();
    const rejectConfirmRef = useRef<(value?: unknown) => void>();
    const { isRefundDetected, isMonitoringOver, startMonitoring } = useMonitorRefund({ initialPurchase });
    const evmModal = useModal(false);

    const readerTerminal = useStripeTerminal();

    useEffect(() => {
        if (isRefundDetected) {
            setRefundStatus({
                message: "Your refund was successfully issued.",
                status: RefundStatusType.SUCCESS,
            });
        }
    }, [isRefundDetected]);

    useEffect(() => {
        // when after 10s waiting/long polling refund success is over, but no new refund is created
        if (isMonitoringOver && !isRefundDetected) {
            setRefundStatus({ message: "Delay with Stripe confirmation", status: RefundStatusType.DELAYED });
        }
    }, [isMonitoringOver, isRefundDetected]);

    useImperativeHandle(ref, () => ({
        async prepare() {
            return true;
        },

        async tokenize() {
            return null;
        },

        async confirm(params?: PaymentInputRefParams) {
            paramRef.current = params;

            return new Promise((resolve, reject) => {
                resolveConfirmRef.current = resolve;
                rejectConfirmRef.current = reject;
                tryProcess();
            });
        },

        closeEMVModal() {
            evmModal.hide();
        },
    }));

    const tryProcess = async () => {
        const clientSecret = paramRef.current?.clientSecret;
        const refundAmount = paramRef.current?.amountDue;
        const last4 = paramRef.current?.last4;

        let count = 1;
        while (getReaderStatus(readerTerminal) !== EMVTerminal.READER_CONNECTED && count < 4) {
            console.log(`EMV: Terminal is ${getReaderStatus(readerTerminal)}, wait and try again. Count: ${count}`);
            await sleep(duration(1, "seconds"));
            count++;
        }

        if (getReaderStatus(readerTerminal) !== EMVTerminal.READER_CONNECTED) {
            // If reader is still not ready, then abort
            flash.warning(
                "Could not communicate with the Reader. Please make sure your reader is online and on the same network as your device. ",
            );
            return handleCancel();
        }

        if (readerTerminal && paramRef.current && clientSecret && refundAmount) {
            evmModal.show();

            try {
                await processEMVPayment(
                    readerTerminal,
                    clientSecret,
                    refundAmount,
                    initialPurchase.currency,
                    setRefundStatus,
                    last4,
                );
                startMonitoring();
                console.log("%cEMV: processEMVPayment is complete", "color:green");
            } catch (error: any) {
                if (error.code !== "canceled") {
                    setRefundStatus({ message: error.message, status: RefundStatusType.DANGER });
                    console.error("EMV: Error processing EMV payment", error);
                }
            }
        } else {
            console.error("EMV: Can't process EMV payment, missing reader or clientSecret", {
                readerTerminal,
                clientSecret,
            });
            setRefundStatus({ message: "Error, cannot process payment", status: RefundStatusType.DANGER });
        }
    };

    const handleCancel = async () => {
        setRefundStatus({ message: "EMV: Cancelling transaction", status: RefundStatusType.DANGER });

        try {
            await readerTerminal?.cancelCollectRefundPaymentMethod();
        } catch {} // We don't care if it fails, an error may come if you try to cancel an un-captured transaction

        evmModal.hide();
        setRefundStatus(defaultRefundStatus);
        rejectConfirmRef.current?.();
    };

    const handleRetry = async () => {
        setRefundStatus(defaultRefundStatus); // Set default status so user knows they can start retrying
        console.log("EMV: Retrying transaction with charge", paramRef.current?.clientSecret);
        if (getReaderStatus(readerTerminal) === EMVTerminal.WAITING) {
            try {
                await readerTerminal?.cancelCollectRefundPaymentMethod();
            } catch {}
        }

        await tryProcess();
    };

    const handleClose = async () => {
        evmModal.hide();
        resolveConfirmRef.current?.();
        // added time delay to avoid showing default state while closing
        setTimeout(() => {
            setRefundStatus(defaultRefundStatus);
        }, 1000);
    };

    return (
        <div>
            <EMVPaymentModal
                last4={paramRef.current?.last4}
                isOpen={evmModal.isVisible}
                refundStatus={refundStatus}
                onCancel={handleCancel}
                onRetry={handleRetry}
                onClose={handleClose}
            />
        </div>
    );
});

interface EMVPaymentModalProps {
    isOpen: boolean;
    refundStatus: { message: string; status: RefundStatusType };
    last4?: string;
    onCancel: () => void;
    onRetry: () => void;
    onClose: () => void;
}

const EMVPaymentModal = ({ isOpen, last4, refundStatus, onCancel, onRetry, onClose }: EMVPaymentModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onCancel}>
            <EmvModalContent
                last4={last4}
                refundStatus={refundStatus}
                onCancel={onCancel}
                onRetry={onRetry}
                onClose={onClose}
            />
        </Modal>
    );
};

const EmvModalContent = ({
    last4,
    refundStatus,
    onCancel,
    onRetry,
    onClose,
}: Except<EMVPaymentModalProps, "isOpen">) => {
    const isPrimary = refundStatus.status === RefundStatusType.PRIMARY;
    const isProcessing = refundStatus.status === RefundStatusType.PROCESSING;
    const isFailed = refundStatus.status === RefundStatusType.DANGER;
    const isDelayed = refundStatus.status === RefundStatusType.DELAYED;
    const isSuccess = refundStatus.status === RefundStatusType.SUCCESS;
    const { title, icon } = processingStatusmap[refundStatus.status];

    return (
        <>
            <Modal.Header>{title}</Modal.Header>
            <Modal.Body>
                <div className="flex flex-col items-center space-y-4 text-center">
                    {icon}

                    {!isFailed && (
                        <div>
                            {refundStatus?.message}
                            {isPrimary && ` *${last4}`}
                        </div>
                    )}

                    {isFailed && (
                        <div>
                            Unable to process refund
                            <div>{refundStatus.message}</div>
                        </div>
                    )}

                    {isDelayed && (
                        <div>
                            The refund was successfully made, but we have not yet received the final confirmation from
                            Stripe. Please check back in a few minutes to verify you see the refund line item. If you
                            still do not see the refund, please contact Xola support.
                        </div>
                    )}
                </div>
            </Modal.Body>
            <Modal.Footer className="flex justify-end">
                {(isPrimary || isProcessing) && (
                    <Button color="danger" disabled={isSuccess || isProcessing} onClick={onCancel}>
                        Cancel Refund
                    </Button>
                )}
                {(isDelayed || isFailed) && <SecondaryButton onClick={onClose}>Close</SecondaryButton>}
                {isSuccess && <Button onClick={onClose}>Close</Button>}
                {isFailed && <Button onClick={onRetry}>Try Again</Button>}
            </Modal.Footer>
        </>
    );
};

const processEMVPayment = async (
    readerTerminal: Terminal,
    clientSecret: string, // will have charge id for refund
    refundAmount: number,
    currency: string,
    setRefundStatus: ({ message, status }: { message: string; status: RefundStatusType }) => void,
    last4?: string,
) => {
    console.log(
        "%cEMV: Doing `readerTerminal.collectRefundPaymentMethod`",
        "color: green",
        readerTerminal.getPaymentStatus(),
    );

    const collectRefundResponse = await readerTerminal.collectRefundPaymentMethod(
        clientSecret,
        Math.round(refundAmount * 100),
        currency,
    );

    setRefundStatus({ message: "Please wait while we process your refund", status: RefundStatusType.PROCESSING });

    if ("error" in collectRefundResponse) {
        throw collectRefundResponse.error as Error;
    } else {
        const refundProcess = await readerTerminal.processRefund();

        console.log("%cEMV: processEMVPayment Processing payment complete", "color:green", refundProcess);

        if ("error" in refundProcess) {
            const errorMessage = refundProcess.error.message;
            // Check for known Interac mismatch error
            if (
                errorMessage.includes("The charge to be refunded") &&
                errorMessage.includes("must both be `interac_present`")
            ) {
                throw new Error(`Please tap the original Interac card ending in *${last4} used for the purchase.`);
            }

            throw refundProcess.error as Error;
        }

        return refundProcess;
    }
};

const processingStatusmap: Record<RefundStatusType, { title: string; icon: React.ReactNode }> = {
    [RefundStatusType.PROCESSING]: { title: "Processing Refund", icon: <Spinner size="large" color="primary" /> },
    [RefundStatusType.PRIMARY]: { title: "Card Required", icon: <EmvImage /> },
    [RefundStatusType.DANGER]: { title: "Error", icon: <CircleCrossIcon className="!h-14 !w-14 text-danger" /> },
    [RefundStatusType.DELAYED]: {
        title: "Refund Processed",
        icon: <WarningFilledIcon className="!h-14 !w-14 text-warning" />,
    },
    [RefundStatusType.SUCCESS]: {
        title: "Successfully Processed Refund",
        icon: <CircleCheckFilledIcon className="!h-14 !w-14 text-green" />,
    },
};

const sleep = async (ms: number): Promise<void> => {
    return new Promise((resolve) => {
        setTimeout(resolve, ms);
    });
};
